import moment from "moment";
import cookie from "cookie-cutter";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import InvoiceDocument from "../app/Renewals/components/Invoice";
import { pdf } from "@react-pdf/renderer";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ToWords } from 'to-words';
import VehicleStatement from "../app/Renewals/components/VehicleStatement";
import menuData from "../constants/routes";


let timer;
const toWords = new ToWords({
  localeCode: 'en-IN',
  converterOptions: {
    currency: false,
    ignoreDecimal: false,
    ignoreZeroCurrency: true,
    doNotAddOnly: false, // 'only' will be added at the end
  },
});

export const setCookie = (name, value, day = 7) => {
    cookie.set(name, value, { expires: new Date((Date.now() + day) * 24 * 60 * 60 * 1000), path: "/" });
}
export const getCookie = (name) => cookie.get(name) || "";
export const removeCookie = (name) => cookie.set(name, "", { expires: new Date(0) });

export const showToast = (message, type = "success", position = "bottom-right") => {
    if (message) {
        toast[type](message, {
            toastId: 'toaster',
            position,
            autoClose: 3000, // Auto close after 3 seconds
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
        });
    }
}

export const validateFields = (fields, setState, onSuccess) => {
    const errors = Object.fromEntries(
        Object.entries(fields).map(([key, [value, type]]) => [key, validateInput(value, type)])
    );

    // Filter out null values (valid inputs)
    const filteredErrors = Object.fromEntries(
        Object.entries(errors).filter(([_, error]) => error)
    );

    // Update state only if there are errors
    if (Object.keys(filteredErrors).length > 0) {
        setState(prev => ({ ...prev, ...filteredErrors }));
    } else {
        if (onSuccess) onSuccess();
    }
};

export const validateInput = (value, inputType) => {
    if (!value) return "Please enter value"
    switch (inputType) {
        case "email":
            const emailPattern = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
            if (!emailPattern.test(value)) {
                return "Invalid email format.";
            }
            break;

        case "phone":
            const phonePattern = /^\+?[1-9]\d{9,14}$/; // Supports international format
            if (!phonePattern.test(value)) {
                return "Invalid phone number. Must be 10-15 digits.";
            }
            break;

        case "imei":
            if (!/^[a-zA-Z0-9]+$/.test(String(value).trim())) {
                return "Invalid IMEI number. Must contain only digits.";
            }
            break;

        case "date":
            if (!isValidDate(value)) {
                return "Invalid date format. Use YYYY-MM-DD.";
            }
            break;

        case "text":
            if (typeof value !== "string" || !value.trim()) {
                return "Invalid string. Must be a non-empty text.";
            }
            break;

        case "password":
            // Password must be at least 8 characters, contain at least one letter, one number, and one special character
            const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
            if (!passwordPattern.test(value)) {
                return "Password must be at least 8 characters long and include at least one letter, one number, and one special character.";
            }
            break;

        case "number":
            if (isNaN(value)) {
                const numberRegex = /^(?!,)(\d{1,3})(,\d{2,3})*(\.\d+)?$/;

                if (!numberRegex.test(value)) {
                    return "Invalid number format.";
                }
            }
            break;

        default:
            return "Unknown validation type.";
    }
    return null; // No error, validation passed
}

// Debounce function to minimize API calls
export const debounceFunction = (func, delay) => {
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

export const isValidObject = (obj) => {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
}

export const commonOnChange = (event, setState) => {
    const { name, value } = event.target;
    setState((prev) => ({ ...prev, [name]: value, [`${name}Err`]: null }))
}

export const commonOnSelect = (event, setState) => {
    const { name, value } = event.target;
    const temp = { [name]: value?.name ?? "", [`${name}Id`]: value?.id ?? "", [`${name}Err`]: null }
    setState(prev => ({ ...prev, ...temp }))
}

export const removeDuplicateObjects = (arr, key) => {
    const seen = new Map();
    return arr.filter(obj => {
        if (!seen.has(obj[key])) {
            seen.set(obj[key], true);
            return true;
        }
        return false;
    });
}


export const getHeaders = (inventoryList) => {
    let headers = [{ name: "Inventory  Type", value: "type" }];
    if (inventoryList.length > 0) {
        inventoryList.forEach(({ typeId }) => {
            const inventoryType = typeId == 20 ? 2 : typeId == 21 ? 3 : 1;
            let temp = [{ name: "Device Model", value: "deviceModal" }, { name: "Sim Provider", value: "simProvider" }, { name: "Sim Status", value: "simStatus" }];
            if (inventoryType === 2) temp = [temp[0]];
            else if (inventoryType === 3) temp = [temp[1], temp[2]];
            headers = [...headers, ...temp];
        })
        headers = [...headers, { name: "Items added", value: "items" }, { name: "Edit", value: "edit" }]
    }
    return removeDuplicateObjects(headers, "name");
}

export function formatDate(dateString, format = "YYYY-MM-DD") {
    const date = moment(dateString);
    if (!date.isValid()) return ""; // Handle invalid dates
    return date.format(format); // Converts to YYYY-MM-DD
}

export const downloadFile = (csvPath, name) => {
    const link = document.createElement("a");
    link.href = csvPath;
    link.download = `${name}.csv`; // optional: filename for the downloaded file
    document.body.appendChild(link); // required for Firefox
    link.click();
    document.body.removeChild(link);
}

export const formatListItems = (list, idKey, nameKey) => {
    return list?.map((item, index) => ({ ...item, ['id']: idKey ? item[idKey] : index, ['name']: nameKey ? item[nameKey] : item })) ?? [];
}

export const downloadCsv = (csvContent, filename) => {
    if (csvContent) {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

export const parseNumber = (str) => {
    try {
        return Number(str) 
    } catch (error) {
        return 0;
    }
}

export const parseByDateRange = (list, range, dataKey = "createdAt") => {
  try {
    const parseDate = (str) => {
      const date = moment(str, 'DD-MM-YYYY', true);
      return date.isValid() ? date : null;
    };

    const start = parseDate(range.startDate)?.startOf('day');
    const end = parseDate(range.endDate)?.endOf('day');

    if (!start || !end) return list;

    return list.filter((item) => {
      const createdAt = moment(item?.[dataKey]);
      return createdAt.isValid() && createdAt.isBetween(start, end, null, '[]'); // inclusive range
    });

  } catch (error) {
    return list;
  }
};

export const reverseDateFormat = (date) => {
    try {
        const [day, month, year] = date.split("-");
        const formattedDate = `${year}-${month}-${day}`;
        return formattedDate;
    } catch (error) {
        return null;
    }
}

export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        showToast("Copied to clipboard!", "info");
    } catch (err) {
        showToast("Failed to copy!", "error");
    }
};

export const generatePassword = (length = 8) => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const specials = "@$!%*?&";

  const letterCount = Math.floor(length * 0.45);
  const numberCount = Math.floor(length * 0.45);
  const specialCount = length - letterCount - numberCount; // To ensure exact total length

  const getRandomChars = (source, count) =>
    Array.from({ length: count }, () => source.charAt(Math.floor(Math.random() * source.length))).join('');

  const letterPart = getRandomChars(letters, letterCount);
  const numberPart = getRandomChars(numbers, numberCount);
  const specialPart = getRandomChars(specials, specialCount);

  return letterPart + numberPart + specialPart;
};

export const DELETE_POP_UP = (callback) => {
    Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to undo this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes'
    }).then((result) => {
        /* Read more about isConfirmed, isDenied below */
        if (result.isConfirmed) callback();
    });
}

export const getDownloadableBlob = async (invoiceDetails, firstInvoice, isQuotation) => {
    let component = <InvoiceDocument data={invoiceDetails} firstInvoice={firstInvoice} isQuotation={isQuotation} />;
    if(invoiceDetails?.type == 2) component = <VehicleStatement isFullPage={true} vehicles={invoiceDetails?.vehicles ?? []} total={invoiceDetails?.total ?? 0} />
    const blob = await pdf(component).toBlob();
    return blob;
}

export const downloadAction = (blob, fileName) => {
    if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }
}

export const handleDownload = async (invoiceDetails, firstInvoice, isQuotation) => {
    const blob = await getDownloadableBlob(invoiceDetails, firstInvoice, isQuotation);

    downloadAction(blob, `${invoiceDetails?.invoiceNo}${invoiceDetails?.type == 2 ? "-vehicle-statement" : ""}.pdf`);
}

export const exportToExcel = (data, filename, totalValue) => {
    try {
        if (!Array.isArray(data) || data.length === 0) {
            throw new Error("Data must be a non-empty array.");
        }

        const keys = Object.keys(data[0]);
        const colCount = keys.length;

        // Convert data to worksheet
        const worksheet = XLSX.utils.json_to_sheet(data);

        // Build total row: empty cells except last two
        const totalRow = new Array(colCount).fill('');
        totalRow[colCount - 2] = 'Total';       // Second-to-last cell
        totalRow[colCount - 1] = totalValue;    // Last cell

        // Append total row
        XLSX.utils.sheet_add_aoa(worksheet, [totalRow], { origin: -1 });

        // Create and export workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(dataBlob, `${filename}.xlsx`);
    } catch (error) {
        // Error handling for Excel export
    }
}

export const exportPurchaseReport = (purchases, filename, companyName, periodText) => {
    try {
        if (!Array.isArray(purchases) || purchases.length === 0) {
            throw new Error("Purchases must be a non-empty array.");
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Column widths
        worksheet.columns = [
            { key: 'invoiceDate', width: 14 }, // A
            { key: 'invoiceNumber', width: 18 }, // B
            { key: 'supplierName', width: 28 }, // C
            { key: 'totalItems', width: 12 }, // D
            { key: 'item', width: 18 }, // E
            { key: 'quantity', width: 10 }, // F
        ];

        // Title
        worksheet.mergeCells('A1:F1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = 'Purchase Report';
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.font = { bold: true, size: 14 };

        // Company and Period rows
        worksheet.getCell('A3').value = 'Company:';
        worksheet.getCell('A3').font = { bold: true };
        worksheet.getCell('B3').value = companyName || '-';

        worksheet.getCell('A4').value = 'Period:';
        worksheet.getCell('A4').font = { bold: true };
        worksheet.getCell('B4').value = periodText || '-';

        // Headers
        worksheet.getCell('A6').value = 'Invoice Date';
        worksheet.getCell('B6').value = 'Invoice Number';
        worksheet.getCell('C6').value = 'Supplier Name';
        worksheet.getCell('D6').value = 'Total Items';
        worksheet.mergeCells('E6:F6');
        const itemHeaderCell = worksheet.getCell('E6');
        itemHeaderCell.value = 'Item Details';
        itemHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
        [ 'A6','B6','C6','D6','E6' ].forEach(addr => {
            const c = worksheet.getCell(addr);
            c.font = { bold: true };
            c.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Sub-headers
        worksheet.getCell('E7').value = 'Item';
        worksheet.getCell('F7').value = 'Quantity';
        [ 'E7','F7' ].forEach(addr => {
            const c = worksheet.getCell(addr);
            c.font = { bold: true };
            c.alignment = { horizontal: 'center', vertical: 'middle' };
        });

        // Data rows
        let rowIndex = 8;
        purchases.forEach((purchase) => {
            const invoiceDate = purchase?.invoice_date ? moment(purchase.invoice_date).format('DD-MMM-YYYY') : '';
            const invoiceNumber = purchase?.invoice_number || '-';
            const supplierName = purchase?.supplier_name || '-';
            const totalItems = purchase?.totalcount || 0;

            const typeCounts = purchase?.type_counts || {};
            const itemEntries = Object.entries(typeCounts);

            if (itemEntries.length === 0) {
                worksheet.getCell(`A${rowIndex}`).value = invoiceDate;
                worksheet.getCell(`B${rowIndex}`).value = invoiceNumber;
                worksheet.getCell(`C${rowIndex}`).value = supplierName;
                worksheet.getCell(`D${rowIndex}`).value = totalItems;
                worksheet.getCell(`E${rowIndex}`).value = '-';
                rowIndex += 1;
                return;
            }

            itemEntries.forEach(([itemName, qty], index) => {
                const isFirst = index === 0;
                worksheet.getCell(`A${rowIndex}`).value = isFirst ? invoiceDate : '';
                worksheet.getCell(`B${rowIndex}`).value = isFirst ? invoiceNumber : '';
                worksheet.getCell(`C${rowIndex}`).value = isFirst ? supplierName : '';
                worksheet.getCell(`D${rowIndex}`).value = isFirst ? totalItems : '';
                worksheet.getCell(`E${rowIndex}`).value = itemName;
                worksheet.getCell(`F${rowIndex}`).value = qty;
                rowIndex += 1;
            });
        });

        // Write and save without changing API (no async signature)
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `${filename}.xlsx`);
        }).catch(() => { /* ignore */ });
    } catch (error) {
        // Silently fail to keep UX consistent with existing util
    }
}

const isValidDate = (input) => {
  const formats = [
    'YYYY-MM-DD',
    'MM/DD/YYYY',
    'DD-MM-YYYY',
    'YYYY/MM/DD',
    'MMM D, YYYY',
    'MMMM D, YYYY',
    moment.ISO_8601
  ];

  const date = moment(input, formats, true); // 'true' enables strict parsing
  return date.isValid();
}

export const amountToWords = (amount) => {
  const num = Number(amount);
  if (isNaN(num)) return 'Invalid amount';
  return toWords.convert(num) || 'Zero only';
}

export function filterListByHeaderState(headers, list, state) {
  if (!state || !headers?.length || !list?.length) return list;

  const activeFilters = Object.entries(state).filter(([header, value]) => value?.trim());

  if (activeFilters.length === 0) return list;

  return list.filter((item) => {
    return activeFilters.every(([header, filterValue]) => {
      const cellValue = item[header];
      return String(cellValue || "").toLowerCase().includes(filterValue.toLowerCase());
    });
  });
}

export const getMenuData = (pages) => {
    const allPages = [];
       pages?.forEach(page => {
        const temp = menuData[page?.page_name]; 
        if(temp) allPages.push({
            ...temp, 
            submenu: page?.children?.length ? getMenuData(page?.children) : []
        })
    });
    return allPages;
}

export const getDefaultPageLink = (pages) => {
    let link = "";
    if(pages?.length > 0) link = pages?.[0]?.link + getDefaultPageLink(pages?.submenu);
    return link;
}
export const getPreviousMonthRangeMoment = (inputDate) => {
  try {
    const date = moment(inputDate);

    if (!date.isValid()) {
      throw new Error("Invalid date provided to getPreviousMonthRangeMoment.");
    }

    const firstDayOfPreviousMonth = date.clone().subtract(1, 'month').startOf('month');
    const lastDayOfPreviousMonth = date.clone().subtract(1, 'month').endOf('month');

    const startDay = firstDayOfPreviousMonth.format('Do');
    const endDay = lastDayOfPreviousMonth.format('Do');
    const monthName = firstDayOfPreviousMonth.format('MMMM');
    const year = firstDayOfPreviousMonth.format('YYYY');

    return `From ${startDay} ${monthName} to ${endDay} ${monthName}, ${year}`;
  } catch (error) {
    console.error("Error in getPreviousMonthRangeMoment:", error.message);
    return "Date range calculation failed.";
  }
};

export const shortenUrl = async (longUrl) => {
  try {
    if(!longUrl) return "";
    const response = await fetch(
      `https://tinyurl.com/api-create.php?url=${encodeURIComponent(longUrl)}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const shortUrl = await response.text();
    return shortUrl;
  } catch (error) {
    console.error("Error shortening URL:", error.message);
    return longUrl; // fallback to original URL
  }
};

export const capitalizeWord = (word) => {
  if (typeof word !== 'string' || word.length === 0) return '';
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const getInitials = (name = "") => {
    try {
        return String(name).split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    } catch (error) {
        return "";   
    }
};

export const encodeAndSave = (key, data) => {
  if (!key) {
    throw new Error("Key is required");
  }

  try {
    // Convert data to string
    const stringData =
      typeof data === "string" ? data : JSON.stringify(data);

    // Encode to Base64
    const encodedData = btoa(encodeURIComponent(stringData));

    // Save
    localStorage.setItem(key, encodedData);

    return true;
  } catch (error) {
    console.error("Failed to encode and save:", error);
    return false;
  }
}

export const decodeAndRead = (key) => {
  try {
    const encodedData = localStorage.getItem(key);
    if (!encodedData) return null;

    const decodedString = decodeURIComponent(atob(encodedData));

    // Try parsing JSON
    try {
      return JSON.parse(decodedString);
    } catch {
      return decodedString;
    }
  } catch (error) {
    console.error("Failed to decode and read:", error);
    return null;
  }
}